export const DEFAULT_PUBLIC_SITE_URL = 'https://carmenlorraine.com';
export const DEFAULT_SHARE_IMAGE = 'https://res.cloudinary.com/di1mzmlts/image/upload/v1767339554/carmen_lorraine_photos/logos/yk0e7dossm5jxaem7pmj.png';

const SITE_URL_KEY = 'public_site_url';

function isLocalHostname(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost'
    || host === '127.0.0.1'
    || host === '0.0.0.0'
    || host === '::1'
    || host.endsWith('.local')
    || host.endsWith('.localhost');
}

function parsePublicUrl(value: unknown): URL | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('/') || trimmed.startsWith('data:')) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    if (parsed.username || parsed.password) return null;
    if (isLocalHostname(parsed.hostname)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function normalizePublicSiteUrl(value: unknown): string | null {
  const parsed = parsePublicUrl(value);
  if (!parsed) return null;
  return `https://${parsed.hostname}`;
}

export function normalizePublicImageUrl(value: unknown): string | null {
  const parsed = parsePublicUrl(value);
  if (!parsed) return null;
  parsed.protocol = 'https:';
  return parsed.toString();
}

const SHARE_FRAME = 'w_1200,h_630,c_pad,b_white,g_center,q_auto,f_auto';

function cloudinaryPublicPath(uploadPath: string): string {
  const versionMatch = uploadPath.match(/(?:^|\/)(v\d+\/.*)$/);
  if (versionMatch && versionMatch.index !== undefined) {
    return versionMatch[1];
  }
  const slash = uploadPath.indexOf('/');
  if (slash > 0) {
    const first = uploadPath.slice(0, slash);
    if (first.includes(',') || /^(?:w_|h_|c_|q_|f_|b_|g_|ar_|e_|fl_)/.test(first)) {
      return uploadPath.slice(slash + 1);
    }
  }
  return uploadPath;
}

/** Fit a share image inside the 1200×630 social frame without cropping it. */
export function fitShareImageUrl(value: unknown): string {
  const safe = normalizePublicImageUrl(value);
  if (!safe) return '';
  const splitAt = safe.indexOf('/upload/');
  if (!safe.includes('res.cloudinary.com') || splitAt < 0) return safe;
  const publicPath = cloudinaryPublicPath(safe.slice(splitAt + '/upload/'.length));
  if (!publicPath) return safe;
  return `${safe.slice(0, splitAt)}/upload/${SHARE_FRAME}/${publicPath}`;
}

export function rememberPublicSiteUrl(value: string): void {
  const normalized = normalizePublicSiteUrl(value);
  if (!normalized || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SITE_URL_KEY, normalized);
  } catch {
    // Sharing still uses the public default.
  }
}

export function getPublicSiteUrl(): string {
  if (typeof window === 'undefined') return DEFAULT_PUBLIC_SITE_URL;
  try {
    return normalizePublicSiteUrl(window.localStorage.getItem(SITE_URL_KEY)) || DEFAULT_PUBLIC_SITE_URL;
  } catch {
    return DEFAULT_PUBLIC_SITE_URL;
  }
}

export function getConfiguredShareImage(): string {
  if (typeof window === 'undefined') return DEFAULT_SHARE_IMAGE;
  try {
    const raw = window.localStorage.getItem('package_share_image');
    if (raw) {
      const parsed = JSON.parse(raw);
      const image = normalizePublicImageUrl(parsed?.imageUrl);
      if (image) return fitShareImageUrl(image);
    }
  } catch {
    // Use the public logo.
  }
  return fitShareImageUrl(DEFAULT_SHARE_IMAGE);
}

export async function refreshPublicShareSettings(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const response = await fetch('/api/share-image');
    if (!response.ok) return;
    const data = await response.json();
    const siteUrl = normalizePublicSiteUrl(data?.publicSiteUrl);
    if (siteUrl) rememberPublicSiteUrl(siteUrl);
    const image = normalizePublicImageUrl(data?.imageUrl);
    if (image) {
      window.localStorage.setItem('package_share_image', JSON.stringify({
        ...data,
        imageUrl: image,
        publicSiteUrl: siteUrl || getPublicSiteUrl(),
      }));
    }
  } catch {
    // Sharing still uses the public default.
  }
}
